import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
interface ScheduleDetail {
  id: string;
  numero_agendamento: string;
  titulo: string;
  descricao: string;
  tipo_agendamento_id: string;
  status: string;
  data_inicio: string;
  data_fim: string;
  veiculo_id: string;
  motorista_id: string;
  local_agendamento: string;
  prioridade: string;
  observacoes: string;
  lembrete_enviado: boolean;
  ativo: boolean;
  concluido: boolean;
  cancelado: boolean;
  alta_prioridade: boolean;
  criado_em: string;
  atualizado_em: string;
  veiculo?: {
    id: string;
    placa: string;
    modelo: string;
    marca: string;
  };
  motorista?: {
    id: string;
    nome: string;
    cpf: string;
  };
  tipo_agendamento_nome?: string;
  tipo_agendamento?: {
    id: string;
    nome: string;
    descricao: string;
  };
}

const ScheduleView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState<ScheduleDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSchedule = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!id) {
        setError('ID do agendamento não fornecido');
        return;
      }

      // Buscar dados reais da API
      const response = await apiService.getSchedule(id);

      if (response.success && response.data) {
        setSchedule(response.data);
      } else {
        setError('Agendamento não encontrado');
      }
    } catch (error) {
      console.error('Erro ao carregar agendamento:', error);
      setError('Erro ao carregar agendamento');
      utils.showNotification('Erro ao carregar agendamento', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };


  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // O backend retorna status/prioridade em minúsculo (agendado, concluido,
  // alta, ...). Normalizamos as chaves para evitar divergência de case.
  const norm = (v?: string): string => (v || '').toLowerCase();

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'agendado': 'status-info',
      'confirmado': 'status-info',
      'concluido': 'status-success',
      'em_andamento': 'status-warning',
      'reagendado': 'status-secondary',
      'cancelado': 'status-danger'
    };
    return statusMap[norm(status)] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'agendado': 'Agendado',
      'confirmado': 'Confirmado',
      'em_andamento': 'Em Andamento',
      'concluido': 'Concluído',
      'cancelado': 'Cancelado',
      'reagendado': 'Reagendado'
    };
    return statusMap[norm(status)] || (status || '-');
  };

  const getPriorityClass = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'urgente': 'status-danger',
      'alta': 'status-danger',
      'normal': 'status-warning',
      'baixa': 'status-info'
    };
    return priorityMap[norm(prioridade)] || 'status-secondary';
  };

  const getPriorityText = (prioridade: string): string => {
    const priorityMap: Record<string, string> = {
      'baixa': 'Baixa',
      'normal': 'Normal',
      'alta': 'Alta',
      'urgente': 'Urgente'
    };
    return priorityMap[norm(prioridade)] || (prioridade || '-');
  };

  const getDaysUntil = (dateString: string): number => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDuration = (start: string, end: string): string => {
    if (!start || !end) return '-';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}min`;
    return `${diffMinutes}min`;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/agendamentos')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Agendamento...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes do agendamento...</p>
        </div>
      </div>
    );
  }

  if (error || !schedule) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/agendamentos')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Agendamento não encontrado'}</h3>
              <p>O agendamento solicitado não pôde ser carregado.</p>
              <button className="btn btn-primary" onClick={() => navigate('/agendamentos')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const daysUntil = getDaysUntil(schedule.data_inicio);
  const duration = getDuration(schedule.data_inicio, schedule.data_fim);

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/agendamentos')}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <h1>Detalhes do Agendamento</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/agendamentos/editar/${schedule.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Cabeçalho com informações principais */}
        <div className="card">
          <div className="card-header">
            <h2>{schedule.titulo}</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className={`status-badge ${getStatusClass(schedule.status)}`}>
                {getStatusText(schedule.status)}
              </span>
              <span className={`status-badge ${getPriorityClass(schedule.prioridade)}`}>
                {getPriorityText(schedule.prioridade)}
              </span>
              <span className="badge badge-secondary">
                {schedule.tipo_agendamento_nome || schedule.tipo_agendamento?.nome || 'Outro'}
              </span>
              {daysUntil > 0 && norm(schedule.status) === 'agendado' && (
                <span className="badge badge-warning">
                  {daysUntil === 1 ? 'Amanhã' : `Em ${daysUntil} dias`}
                </span>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Número do Agendamento:</label>
                <span className="info-value">{schedule.numero_agendamento}</span>
              </div>
              <div className="info-item">
                <label>Descrição:</label>
                <span className="info-value">{schedule.descricao}</span>
              </div>
              <div className="info-item">
                <label>Data de Início:</label>
                <span className="info-value">{formatDateTime(schedule.data_inicio)}</span>
              </div>
              <div className="info-item">
                <label>Data de Término:</label>
                <span className="info-value">{formatDateTime(schedule.data_fim)}</span>
              </div>
              <div className="info-item">
                <label>Duração:</label>
                <span className="info-value">{duration}</span>
              </div>
              <div className="info-item">
                <label>Local:</label>
                <span className="info-value">{schedule.local_agendamento}</span>
              </div>
              <div className="info-item">
                <label>Veículo:</label>
                <span className="info-value">
                  {schedule.veiculo ? `${schedule.veiculo.placa} - ${schedule.veiculo.marca} ${schedule.veiculo.modelo}` : schedule.veiculo_id}
                </span>
              </div>
              <div className="info-item">
                <label>Motorista:</label>
                <span className="info-value">
                  {schedule.motorista ? schedule.motorista.nome : schedule.motorista_id}
                </span>
              </div>
              <div className="info-item">
                <label>Tipo:</label>
                <span className="info-value">
                  {schedule.tipo_agendamento_nome || schedule.tipo_agendamento?.nome || 'Outro'}
                </span>
              </div>
              <div className="info-item">
                <label>Status:</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(schedule.status)}`}>
                    {getStatusText(schedule.status)}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Prioridade:</label>
                <span className="info-value">
                  <span className={`status-badge ${getPriorityClass(schedule.prioridade)}`}>
                    {getPriorityText(schedule.prioridade)}
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Lembrete Enviado:</label>
                <span className="info-value">
                  {schedule.lembrete_enviado ? 'Sim' : 'Não'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Observações */}
        {schedule.observacoes && (
          <div className="card" style={{ marginTop: '20px' }}>
            <div className="card-header">
              <h3>Observações</h3>
            </div>
            <div className="card-body">
              <p style={{ color: '#7f8c8d', lineHeight: '1.6', margin: 0 }}>
                {schedule.observacoes}
              </p>
            </div>
          </div>
        )}

        {/* Informações adicionais */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações Adicionais</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Ativo:</label>
                <span className="info-value">{schedule.ativo ? 'Sim' : 'Não'}</span>
              </div>
              <div className="info-item">
                <label>Concluído:</label>
                <span className="info-value">{schedule.concluido ? 'Sim' : 'Não'}</span>
              </div>
              <div className="info-item">
                <label>Cancelado:</label>
                <span className="info-value">{schedule.cancelado ? 'Sim' : 'Não'}</span>
              </div>
              <div className="info-item">
                <label>Alta Prioridade:</label>
                <span className="info-value">{schedule.alta_prioridade ? 'Sim' : 'Não'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Informações de auditoria */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações de Auditoria</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Criado em:</label>
                <span className="info-value">{formatDateTime(schedule.criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Atualizado em:</label>
                <span className="info-value">{formatDateTime(schedule.atualizado_em)}</span>
              </div>
              <div className="info-item">
                <label>ID do Agendamento:</label>
                <span className="info-value">{schedule.id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/agendamentos')}
          >
            <i className="fas fa-list"></i>
            Voltar para Lista
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/agendamentos/editar/${schedule.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar Agendamento
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleView;
